import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { router } from '@inertiajs/react';
import Paginator from '../../components/Paginator';
import { Button } from '../../components/ui/button';
import { DatePicker } from '../../components/ui/date-picker';
import { Select } from '../../components/ui/select';
import { SearchInput } from '../../components/SearchInput';
import {
    Combobox,
    ComboboxChip,
    ComboboxChips,
    ComboboxChipsInput,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
    ComboboxTrigger,
    ComboboxValue,
    useComboboxAnchor,
} from '../../components/ui/combobox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { useOrderDrawer, OrderDetailDrawer } from '../../components/order/OrderDetailDrawer';
import { DetailCard } from '../../components/DetailCard';
import { QuickViewButton } from '../../components/order/QuickViewButton';
import { OrderColumnSettings } from '../../components/order/OrderColumnSettings';
import { ORDER_COLUMNS, resolveColumns } from '../../components/order/orderColumns';
import { useTrans } from '../../hooks/useTrans';

const LOCAL_ORDER_FIELDS = [
    { key: 'date', label: 'Aangemaakt', className: 'whitespace-nowrap', render: (order) => order.date },
    { key: 'order_id', label: 'Order ID', render: (order, { pageTranslations }) => order.external_order_id || <em>{pageTranslations.externalIdUnknown}</em> },
    { key: 'debtor', label: 'Debiteur', render: (order) => order.debtor },
    { key: 'from', label: 'Van', render: (order) => order.pickup_city },
    { key: 'to', label: 'Naar', render: (order) => order.delivery_city },
    { key: 'status', label: 'Status', render: (order) => order.status },
    { key: 'transport_type', label: 'Transporttype', render: (order) => order.transport_type },
    { key: 'reference', label: 'Referentie', render: (order) => order.reference },
    { key: 'order_reference', label: 'Klantreferentie', render: (order) => order.order_reference },
];

export default function OrdersIndex({ filters, years, orderStatuses, localOrders, remoteOrders, remoteOrdersError, isFiltered, pageTranslations, visibleColumns }) {
    const translate = useTrans();
    const [columnKeys, setColumnKeys] = useState(() => resolveColumns(visibleColumns).map((column) => column.key));
    const columns = resolveColumns(columnKeys);
    const [showFilters, setShowFilters] = useState(isFiltered);
    const [showMoreFilters, setShowMoreFilters] = useState(
        !!(filters.pickup_start_date || filters.pickup_end_date || filters.delivery_start_date || filters.delivery_end_date || filters.order_status.length)
    );
    const drawer = useOrderDrawer();

    const [form, setForm] = useState({
        query: filters.query || '',
        start_date: filters.start_date || '',
        end_date: filters.end_date || '',
        pickup_start_date: filters.pickup_start_date || '',
        pickup_end_date: filters.pickup_end_date || '',
        delivery_start_date: filters.delivery_start_date || '',
        delivery_end_date: filters.delivery_end_date || '',
        year: filters.year || '',
        order_status: filters.order_status || [],
        order_column: filters.order_column || 'date',
        order_direction: filters.order_direction || 'desc',
    });

    const [statusQuery, setStatusQuery] = useState('');
    const statusAnchor = useComboboxAnchor();

    function handleSubmit(e) {
        e.preventDefault();
        router.get('/orders', form, { preserveState: true });
    }

    function handleExport(e) {
        e.preventDefault();
        router.post('/orders/export', form);
    }

    function handleChange(e) {
        setForm({ ...form, [e.target.name]: e.target.value });
    }

    function handleClearFilters() {
        router.get('/orders');
    }

    function handleSort(column, direction = form.order_column === column && form.order_direction === 'asc' ? 'desc' : 'asc') {
        const newForm = { ...form, order_column: column, order_direction: direction };
        setForm(newForm);
        router.get('/orders', newForm, { preserveState: true });
    }

    function SortableHead({ column, children, className = '' }) {
        const isActive = form.order_column === column;
        const arrow = isActive ? (form.order_direction === 'asc' ? '↑' : '↓') : '';
        return (
            <TableHead
                className={`py-5 px-2.5 text-[#003545] font-normal text-sm cursor-pointer select-none hover:text-[#0e8e6a] ${className}`}
                onClick={() => handleSort(column)}
            >
                <span className="underline">{children}</span>{arrow}
            </TableHead>
        );
    }

    const selectedStatuses = orderStatuses.filter(status => form.order_status.includes(status.value));

    const hasLocalOrders = localOrders && localOrders.length > 0;
    const hasRemoteOrders = remoteOrders?.data && remoteOrders.data.length > 0;
    const renderContext = { translate, pageTranslations };
    const emptyMessage = remoteOrdersError || (isFiltered
        ? translate('Geen orders gevonden met de huidige filtering.')
        : translate('U heeft nog geen orders geplaatst.'));

    return (
        <div className="px-4 min-w-0">
            <div className="bg-white rounded-[3px] border border-[#bbb] min-w-0">
                <div className="p-4 sm:p-[30px] pb-0 sm:pb-0">
                    <h3 className="hidden md:block text-2xl font-bold text-[#003545] mb-6">{translate('Orders filteren')}</h3>
                    <button
                        type="button"
                        onClick={() => setShowFilters(!showFilters)}
                        aria-expanded={showFilters}
                        aria-controls="order-filters"
                        className={`md:hidden flex w-full items-center justify-between text-2xl font-bold text-[#003545] cursor-pointer ${showFilters ? 'mb-6' : ''}`}
                    >
                        {translate('Orders filteren')}
                        <ChevronDown className={`size-6 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                    </button>
                </div>

                <div id="order-filters" className={`${showFilters ? '' : 'hidden'} md:block px-4 sm:px-[30px]`}>
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-end gap-4 mb-4">
                            <label className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm font-bold text-[#003545] whitespace-nowrap">
                                {translate('Aangemaakt')}
                                <DatePicker name="start_date" value={form.start_date} onChange={handleChange} className="w-full sm:w-[110px]" captionLayout="dropdown" startYear={2015} endYear={new Date().getFullYear()} />
                            </label>
                            <label className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm font-bold text-[#003545] whitespace-nowrap">
                                {translate('tot en met')}
                                <DatePicker name="end_date" value={form.end_date} onChange={handleChange} className="w-full sm:w-[110px]" captionLayout="dropdown" startYear={2015} endYear={new Date().getFullYear()} />
                            </label>
                            <Button type="button" variant="primary" className="w-full lg:w-auto" onClick={() => setShowMoreFilters(!showMoreFilters)}>
                                {showMoreFilters ? translate('Minder filters') : translate('Meer filters')}
                            </Button>
                            <label className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm font-bold text-[#003545] whitespace-nowrap">
                                {translate('Jaar')}
                                <Combobox
                                    items={years.map(year => String(year))}
                                    value={form.year || null}
                                    onValueChange={(value) => setForm({ ...form, year: value ?? '' })}
                                >
                                    <ComboboxTrigger render={
                                        <Button variant="outline" className="w-full sm:w-28 font-normal justify-between rounded-sm border-[#bbb] shadow-none">
                                            <ComboboxValue placeholder={translate('Toon alles')} />
                                        </Button>
                                    } />
                                    <ComboboxContent>
                                        <ComboboxInput showTrigger={false} placeholder={translate('Zoek jaar...')} />
                                        <ComboboxEmpty>{translate('Geen jaren gevonden.')}</ComboboxEmpty>
                                        <ComboboxList>
                                            {(year) => (
                                                <ComboboxItem key={year} value={year}>
                                                    {year}
                                                </ComboboxItem>
                                            )}
                                        </ComboboxList>
                                    </ComboboxContent>
                                </Combobox>
                            </label>
                            <div className="grid grid-cols-1 sm:flex sm:items-end gap-2 lg:ml-auto col-span-full lg:col-span-1">
                                <SearchInput
                                    name="query"
                                    value={form.query}
                                    onChange={handleChange}
                                    onClear={() => {
                                        const newForm = { ...form, query: '' };
                                        setForm(newForm);
                                        router.get('/orders', newForm, { preserveState: true });
                                    }}
                                    showClear={!!filters.query}
                                    placeholder={translate('Zoeken')}
                                    className="w-full sm:w-48"
                                />
                                <Button variant="primary" type="submit" className="w-full sm:w-auto">{translate('Zoeken')}</Button>
                                <Button variant="primary" type="button" className="w-full sm:w-auto" onClick={handleExport}>{translate('Exporteer')}</Button>
                            </div>
                        </div>

                        {showMoreFilters && (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center gap-4 mb-4">
                                    <label className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm font-bold text-[#003545] whitespace-nowrap">
                                        {translate('Ophaaldatum')}
                                        <DatePicker name="pickup_start_date" value={form.pickup_start_date} onChange={handleChange} className="w-full sm:w-[110px]" captionLayout="dropdown" startYear={2015} endYear={new Date().getFullYear()} />
                                    </label>
                                    <label className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm font-bold text-[#003545] whitespace-nowrap">
                                        {translate('tot en met')}
                                        <DatePicker name="pickup_end_date" value={form.pickup_end_date} onChange={handleChange} className="w-full sm:w-[110px]" captionLayout="dropdown" startYear={2015} endYear={new Date().getFullYear()} />
                                    </label>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center gap-4 mb-4">
                                    <label className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm font-bold text-[#003545] whitespace-nowrap">
                                        {translate('Afleverdatum')}
                                        <DatePicker name="delivery_start_date" value={form.delivery_start_date} onChange={handleChange} className="w-full sm:w-[110px]" captionLayout="dropdown" startYear={2015} endYear={new Date().getFullYear()} />
                                    </label>
                                    <label className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm font-bold text-[#003545] whitespace-nowrap">
                                        {translate('tot en met')}
                                        <DatePicker name="delivery_end_date" value={form.delivery_end_date} onChange={handleChange} className="w-full sm:w-[110px]" captionLayout="dropdown" startYear={2015} endYear={new Date().getFullYear()} />
                                    </label>
                                </div>
                                <div className="grid grid-cols-1 sm:flex sm:flex-wrap sm:items-center gap-4 mb-4">
                                    <label className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-sm font-bold text-[#003545] whitespace-nowrap">
                                        {translate('Status')}
                                        <Combobox
                                            multiple
                                            items={orderStatuses}
                                            value={selectedStatuses}
                                            onValueChange={(items) => {
                                                setForm({ ...form, order_status: items.map(item => item.value) });
                                                setStatusQuery('');
                                            }}
                                            inputValue={statusQuery}
                                            onInputValueChange={(value) => setStatusQuery(value)}
                                            itemToStringValue={(item) => item.label}
                                            onOpenChange={(open, eventDetails) => {
                                                if (!open && eventDetails.reason === 'item-press') {
                                                    eventDetails.cancel();
                                                }
                                            }}
                                        >
                                            <ComboboxChips ref={statusAnchor} className="w-full sm:w-64 rounded-sm border-[#bbb] shadow-none">
                                                <ComboboxValue>
                                                    {(selected) => selected.map((item) => (
                                                        <ComboboxChip key={item.value}>
                                                            {item.label}
                                                        </ComboboxChip>
                                                    ))}
                                                </ComboboxValue>
                                                <ComboboxChipsInput
                                                    className="font-normal"
                                                    placeholder={selectedStatuses.length === 0 ? translate('Toon alle statussen') : ''}
                                                />
                                            </ComboboxChips>
                                            <ComboboxContent anchor={statusAnchor}>
                                                <ComboboxEmpty>{translate('Geen resultaten.')}</ComboboxEmpty>
                                                <ComboboxList>
                                                    {(item) => (
                                                        <ComboboxItem key={item.value} value={item}>
                                                            {item.label}
                                                        </ComboboxItem>
                                                    )}
                                                </ComboboxList>
                                            </ComboboxContent>
                                        </Combobox>
                                    </label>
                                </div>
                            </>
                        )}

                        {isFiltered && (
                            <div className="mb-4">
                                <Button variant="danger" type="button" className="w-full sm:w-auto" onClick={handleClearFilters}>
                                    {translate('Filter verwijderen')}
                                </Button>
                            </div>
                        )}
                    </form>
                </div>

                <div className="px-4 sm:px-[30px] pb-4 sm:pb-[30px] overflow-hidden">
                    {hasLocalOrders && (
                        <>
                            <h3 className="text-2xl font-bold text-[#003545] mb-4 mt-6">{translate('Zojuist aangemaakte orders')}</h3>
                            <div className="hidden md:block overflow-x-auto">
                                <Table>
                                    <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                                        <TableRow>
                                            {LOCAL_ORDER_FIELDS.map((field) => (
                                                <TableHead key={field.key} className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate(field.label)}</TableHead>
                                            ))}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {localOrders.map((order, index) => (
                                            <TableRow key={order.uuid} className={index % 2 === 1 ? 'bg-[#f5f5f5]' : ''}>
                                                {LOCAL_ORDER_FIELDS.map((field) => (
                                                    <TableCell key={field.key} className={`py-5 px-2.5 ${field.className || ''}`}>
                                                        {field.render(order, renderContext)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            <ul className="md:hidden border-y border-[#d4d4d4]">
                                {localOrders.map((order, index) => (
                                    <DetailCard key={order.uuid} item={order} fields={LOCAL_ORDER_FIELDS} context={renderContext} striped={index % 2 === 1} />
                                ))}
                            </ul>
                        </>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-2 mb-4 mt-6">
                        <h3 className="text-2xl font-bold text-[#003545]">{translate('Orderoverzicht')}</h3>
                        <OrderColumnSettings visibleColumns={columnKeys} onChange={setColumnKeys} />
                    </div>
                    <div className="md:hidden flex items-end gap-2 mb-4">
                        <label className="flex flex-col gap-1 grow text-sm font-bold text-[#003545]">
                            {translate('Sorteren op')}
                            <Select value={form.order_column} onChange={(e) => handleSort(e.target.value, form.order_direction)} className="font-normal">
                                {ORDER_COLUMNS.map((column) => (
                                    <option key={column.key} value={column.key}>{translate(column.label)}</option>
                                ))}
                            </Select>
                        </label>
                        <Select
                            value={form.order_direction}
                            onChange={(e) => handleSort(form.order_column, e.target.value)}
                            className="w-auto"
                            aria-label={translate('Sorteervolgorde')}
                        >
                            <option value="asc">{translate('Oplopend')}</option>
                            <option value="desc">{translate('Aflopend')}</option>
                        </Select>
                    </div>
                    <div className="hidden md:block overflow-x-auto">
                        <Table>
                            <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                                <TableRow>
                                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm w-0"></TableHead>
                                    {columns.map((column) => (
                                        <SortableHead key={column.key} column={column.key}>{translate(column.label)}</SortableHead>
                                    ))}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {hasRemoteOrders ? (
                                    remoteOrders.data.map((order, index) => (
                                        <TableRow key={order.id} className={index % 2 === 1 ? 'bg-[#f5f5f5]' : ''}>
                                            <TableCell className="py-5 px-2.5">
                                                {order.internal_id && (
                                                    <QuickViewButton onClick={() => drawer.openDrawer(order.internal_id)} title={translate('Snelweergave')} />
                                                )}
                                            </TableCell>
                                            {columns.map((column) => (
                                                <TableCell key={column.key} className={`py-5 px-2.5 ${column.className || ''}`}>
                                                    {column.render(order, renderContext)}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length + 1} className="py-5 px-2.5 text-center text-gray-500">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="md:hidden">
                        {hasRemoteOrders ? (
                            <ul className="border-y border-[#d4d4d4]">
                                {remoteOrders.data.map((order, index) => (
                                    <DetailCard
                                        key={order.id}
                                        item={order}
                                        fields={columns}
                                        context={renderContext}
                                        striped={index % 2 === 1}
                                        action={order.internal_id && <QuickViewButton onClick={() => drawer.openDrawer(order.internal_id)} title={translate('Snelweergave')} />}
                                    />
                                ))}
                            </ul>
                        ) : (
                            <p className="py-5 px-2.5 text-center text-gray-500">{emptyMessage}</p>
                        )}
                    </div>
                    {remoteOrders?.data?.some(order => order.source !== 'webshop') && (
                        <p className="text-sm text-gray-500 italic mt-2">* {translate('Betreft een automatisch ingediende order.')}</p>
                    )}

                    <Paginator links={remoteOrders?.links} />
                </div>
            </div>

            <OrderDetailDrawer {...drawer} />
        </div>
    );
}
